import { PreviewMessageType } from '@webcontainer/api';
import dice from 'fast-dice-coefficient';
import stripAnsi from 'strip-ansi';
import type { PotentialProblem } from '~/types/chat';

export function fuzzyCompare(_prev: string, _next: string) {
  const [prev, next] = [_prev, _next].map(normalize);

  return dice(prev, next) > 0.85;
}

export function normalize(text: string) {
  return text.replace(/[\d.]+ms| \(x\d+\)|\d+:\d+(:\d+)(\s+(AM|PM))?/g, '');
}

// used to compare and filter errors
export function stringifyProblem(problem: PotentialProblem) {
  if (problem.type === 'shell' || problem.type === 'terminal') {
    return stripAnsi(problem.output).trim();
  }

  if (problem.type === 'supabase-migration') {
    return problem.message;
  }

  if (problem.type === 'supabase-function') {
    return problem.functionSlug;
  }

  const { error } = problem;

  if (error.type === PreviewMessageType.ConsoleError) {
    if (error.args.length === 0) {
      return '';
    }

    // ignore warnings
    if (error.args[0].startsWith?.('Warning:') && !error.args[0].toLowerCase?.().includes('error')) {
      return '';
    }

    // ignore HMR messages
    if (error.args[0].includes?.('syntax errors or importing non-existent modules. (see errors above)')) {
      return '';
    }

    const args = JSON.stringify(error.args);

    // we have to ignore empty errors because we can't do anything with them
    if (args === '[{}]') {
      return '';
    }

    return args;
  }

  return error.stack ?? '';
}

const preTransformPattern = /\[vite\] Pre-transform error:/;
const errorPattern = new RegExp(preTransformPattern.source + '(.*?)$', 'm');

export function filterVitePreTransformDupes(problems: PotentialProblem[]) {
  const preTransforms = problems.filter((problem) => {
    return problem.type === 'terminal' && preTransformPattern.test(stripAnsi(problem.output));
  });

  const pruned = new Set<PotentialProblem>();

  if (preTransforms.length > 0) {
    for (const preTransformProblem of preTransforms) {
      if (preTransformProblem.type !== 'terminal') {
        continue;
      }

      const errorMessageMatch = stripAnsi(preTransformProblem.output).match(errorPattern);

      if (errorMessageMatch) {
        const [, errorMessage] = errorMessageMatch;

        const dupes = problems.filter((problem) => {
          return (
            problem !== preTransformProblem &&
            problem.type === 'terminal' &&
            stripAnsi(problem.output).includes(errorMessage)
          );
        });

        if (dupes.length > 0) {
          // retain the first, drop the pretransform and the rest
          dupes.slice(1).forEach((dupe) => {
            pruned.add(dupe);
          });

          pruned.add(preTransformProblem);
        }
      }
    }
  }

  return problems.filter((problem) => !pruned.has(problem));
}

const expoSdkUpgradePattern = new RegExp(
  [
    /Either upgrade this project to SDK \d+\.\d+\.\d+ or install a version of Expo Go that is compatible with your project\./,

    /Project is incompatible with this version of Expo Go/,
  ]
    .map((pattern) => pattern.source)
    .join('|'),
  'iu',
);

export function isExpoSDKUpgradeError(text: string) {
  return expoSdkUpgradePattern.test(text);
}

interface FieldError {
  field: string;
  message: string;
}

export interface ErrorWithMessages {
  errors: Array<unknown>;
}

const isFieldError = (error: unknown): error is FieldError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'field' in error &&
    'message' in error &&
    typeof error.field === 'string' &&
    typeof error.message === 'string'
  );
};

const isErrorWithMessages = (error: unknown): error is ErrorWithMessages => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  if (!('errors' in error) || !Array.isArray(error.errors)) {
    return false;
  }

  return true;
};

const getHumanizedInput = (input: string): string => {
  if (input === 'slug') {
    // we want to show the term 'name' instead of 'slug'
    return 'name';
  }

  return input;
};

const humanizeErrorMessage = (input: string): string => {
  const humanizedInput = getHumanizedInput(input);
  const words = humanizedInput.split('_');
  const lowercaseWords = words.map((word) => word.toLowerCase());

  if (lowercaseWords.length > 0) {
    lowercaseWords[0] = lowercaseWords[0][0].toUpperCase() + lowercaseWords[0].slice(1);
  }

  return lowercaseWords.join(' ');
};

export const getMessageFromApiError = (
  errorObject: unknown,
  defaultMessage: string = 'Something went wrong.',
  sep: string = ', ',
): string => {
  if (!isErrorWithMessages(errorObject)) {
    return defaultMessage;
  }

  const { errors } = errorObject;

  const messages: string[] = [];

  errors.forEach((error) => {
    if (isFieldError(error)) {
      messages.push(
        error.field && error.field !== 'base' ? `${humanizeErrorMessage(error.field)} ${error.message}` : error.message,
      );
    } else if (typeof error === 'string') {
      messages.push(error);
    }
  });

  return messages.length > 0 ? messages.join(sep) : defaultMessage;
};
